import useResizeObserver from '@react-hook/resize-observer';
import * as d3 from 'd3';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import {
  Box3,
  BufferGeometry,
  Color,
  Line,
  LineSegments,
  MathUtils,
  MeshBasicMaterial,
  Vector2,
  Vector3,
} from 'three';
import { throttle } from 'underscore';

import { FeatureFlagsManager } from '../../../../../utils/core/feature-flags-manager';
import { consequetivePairIterator, pairIterator } from '../../../../../utils/generators';
import { t } from '../../../../../utils/i18-next';
import { isValidNumber } from '../../../../../utils/math/number';
import { bufferAttributeUtils } from '../../../../../utils/three/buffer-attribute';
import { isDefined } from '../../../../../utils/variables';
import { AssetVisibilityManager } from '../../../../../viewer/asset-visibility-manager';
import { CoordinateManager } from '../../../../../viewer/coordinate-manager';
import { ElementObj } from '../../../../../viewer/elements/element-obj';
import { Geometry } from '../../../../../viewer/elements/geometry';
import { publish, SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import {
  AssetMeshBVHData,
  ProfileSectionGraphMode,
  ProfileSectionMeasureMode,
  ProfileSectionTool,
} from '../../../../../viewer/tools/profile-section-tool';
import {
  Coordinates,
  getPolyLineLength,
  mathematicalPrecision,
  toVector2,
} from '../../../../../viewer/utilities/math-helpers';
import { useAssets } from '../../../../hooks/use-assets';
import { useDialog } from '../../../../hooks/use-dialog';
import { useNonce } from '../../../../hooks/use-nonce';
import { useSubscribe } from '../../../../hooks/use-subscribe';
import { useUnitSymbols } from '../../../../hooks/use-unit-symbols';
import { ThinScrollbar } from '../../../../styles/thin-scrollbar';
import { Button } from '../../../button/button';
import { ColorDialog, ColorIcon, RgbArray } from '../../../color-picker-dialog/color-picker-dialog';
import { Dialog } from '../../../dialog/dialog';
import { DropdownSuggestion } from '../../../dropdown-suggestion/dropdown-suggesion';
import { Icon } from '../../../icon/icon';
import { IconPanel } from '../../../icon-panel/icon-panel';
import { InfoBox } from '../../../info-box/info-box';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Select } from '../../../select/select';
import { RightPanelHeader } from '../../../skyview/right-menu/components/right-menu-header';
import { Stack } from '../../../stack/stack';
import { TextBox } from '../../../text-box/text-box';
import { ExportProfileSectionReportDialog } from './export-profile-section-report-dialog';
import { getSectionLabel, profileSectionGraphUtils } from './profile-section-graph';

type Props = {
  tool: ProfileSectionTool;
};

export type GraphLine = {
  id: string;
  name: string;
  color: Color;
  /**
   * The 3D points of the graph line. These needs to be projected onto the 2D graph.
   */
  segments: { original: [Vector3, Vector3]; projected: [Vector2, Vector2] }[];
};

/**
 * Checks if the point is within the triangle if projected onto a 2D space.
 */
function pointInTriangleProjected(p: Vector2, triangle2d: [Vector3, Vector3, Vector3]): boolean {
  const area = (a: Vector2, b: Vector3, c: Vector3) =>
    (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y);
  const sign1 = area(p, triangle2d[0], triangle2d[1]) >= 0;
  const sign2 = area(p, triangle2d[1], triangle2d[2]) >= 0;
  const sign3 = area(p, triangle2d[2], triangle2d[0]) >= 0;
  return sign1 === sign2 && sign2 === sign3;
}

function intersectSegments2D(
  segA: [Vector2, Vector2],
  segB: [Vector3, Vector3],
  mas: number,
): { point: Vector3; t: number } | undefined {
  const [p1, p2] = segA;
  const [q1, q2] = segB;

  const r = p2.clone().sub(p1);
  const s = q2.clone().sub(q1);

  const rxs = r.x * s.y - r.y * s.x;
  const qp = {
    x: q1.x - p1.x,
    y: q1.y - p1.y,
  };
  const qpxr = qp.x * r.y - qp.y * r.x;

  // Parallel or colinear
  if (Math.abs(rxs) < Number.EPSILON) {
    return undefined;
  }

  const t = (qp.x * s.y - qp.y * s.x) / rxs;
  const u = qpxr / rxs;

  if (t < 0 || t > 1 + 0 || u < 0 || u > 1 + 0) {
    return undefined;
  }

  const intersection2D = p1.clone().add(r.multiplyScalar(t));

  // Interpolate Z using 'u' on segB
  const z = MathUtils.lerp(q1.z, q2.z, u);

  return { point: new Vector3(intersection2D.x, intersection2D.y, z + mas), t };
}

function barycentricWeights(
  p: Vector2,
  a: Vector3,
  b: Vector3,
  c: Vector3,
): [number, number, number] {
  const v0 = new Vector2(b.x - a.x, b.y - a.y);
  const v1 = new Vector2(c.x - a.x, c.y - a.y);
  const v2 = new Vector2(p.x - a.x, p.y - a.y);

  const d00 = v0.dot(v0);
  const d01 = v0.dot(v1);
  const d11 = v1.dot(v1);
  const d20 = v2.dot(v0);
  const d21 = v2.dot(v1);

  const denom = d00 * d11 - d01 * d01;
  const v = (d11 * d20 - d01 * d21) / denom;
  const w = (d00 * d21 - d01 * d20) / denom;
  const u = 1 - v - w;

  return [u, v, w];
}

/**
 * Gets the intersecting edge of the triangle based on 2D-intersection towards the segment.
 * If there segment only partially intersects the triangle then the partial edge will be returned.
 *
 * Will return undefined if there is no intersection.
 */
function getEdge(
  segment: [Vector2, Vector2],
  triangle: [Vector3, Vector3, Vector3],
  mas: number,
): { edge: [Vector3, Vector3]; t: [number, number] } | undefined {
  const intersections: Vector3[] = [];
  const ts: number[] = [];

  const addPoint = (newPoint?: Vector3, t?: number) => {
    // Skip if the segment already contains 2 points.
    if (intersections.length > 1 || !newPoint || !isDefined(t)) {
      return;
    }

    ts.push(t);
    intersections.push(newPoint);
  };

  /**
   * Projects the 2D point onto the triangle using barycentric weights.
   */
  const projectPointOntoTriangle = (point: Vector2) => {
    const [u, v, w] = barycentricWeights(point, triangle[0], triangle[1], triangle[2]);
    const z = u * triangle[0].z + v * triangle[1].z + w * triangle[2].z;

    if (!Number.isFinite(z)) {
      return;
    }

    return new Vector3(point.x, point.y, z + mas);
  };

  // Check if endpoints are inside triangle
  if (pointInTriangleProjected(segment[0], triangle)) {
    addPoint(projectPointOntoTriangle(segment[0]), 0);
  }
  if (pointInTriangleProjected(segment[1], triangle)) {
    addPoint(projectPointOntoTriangle(segment[1]), 1);
  }

  if (intersections.length < 2) {
    for (const edge of consequetivePairIterator(triangle, true)) {
      const result = intersectSegments2D(segment, edge, mas);
      if (result) {
        addPoint(result.point, result.t);
      }
    }
  }

  if (intersections.length < 2) {
    return undefined;
  }

  return {
    edge: intersections as [Vector3, Vector3],
    t: ts as [number, number],
  };
}

export function getIntersectedEdges(
  coordinates: Coordinates[],
  assetMeshBvh: AssetMeshBVHData,
  mas: number,
  xOffset: number,
): { original: [Vector3, Vector3]; projected: [Vector2, Vector2] }[] {
  const worldPositions = coordinates.map((x) =>
    CoordinateManager.getInstance().projectCoordsToLocal(x).setZ(0),
  );

  const segments: [Vector3, Vector3][] = [];
  for (const segment of consequetivePairIterator(worldPositions)) {
    segments.push(segment);
  }

  const aabb = new Box3();
  const v0 = new Vector3();
  const v1 = new Vector3();
  const v2 = new Vector3();

  const edges: { original: [Vector3, Vector3]; projected: [Vector2, Vector2] }[] = [];
  for (const { mesh, bvh } of assetMeshBvh.data) {
    const inv = mesh.matrixWorld.clone().invert();

    let totalDistance = -xOffset;
    for (const [world0, world1] of segments) {
      const p0 = world0.clone().applyMatrix4(inv);
      const p1 = world1.clone().applyMatrix4(inv);
      const segmentLength = world0.distanceTo(world1);
      aabb.setFromPoints([p0, p1]);
      aabb.min.setZ(Number.MIN_SAFE_INTEGER);
      aabb.max.setZ(Number.MAX_SAFE_INTEGER);

      const segment2d: [Vector2, Vector2] = [toVector2(world0), toVector2(world1)];

      bvh.shapecast({
        // Prune nodes not touched by segment AABB.
        intersectsBounds: (box) => box.intersectsBox(aabb),

        // Called per triangle in touched leaves.
        intersectsTriangle: (triLocal) => {
          v0.copy(triLocal.a).applyMatrix4(mesh.matrixWorld);
          v1.copy(triLocal.b).applyMatrix4(mesh.matrixWorld);
          v2.copy(triLocal.c).applyMatrix4(mesh.matrixWorld);

          const result = getEdge(segment2d, [v0, v1, v2], mas);
          if (result) {
            edges.push({
              original: result.edge,
              projected: [
                new Vector2(totalDistance + result.t[0] * segmentLength, result.edge[0].z),
                new Vector2(totalDistance + result.t[1] * segmentLength, result.edge[1].z),
              ],
            });
          }

          return false;
        },
      });

      totalDistance += segmentLength;
    }
  }

  return edges;
}

function useLengthProfileResult(
  tool: ProfileSectionTool,
  mode: ProfileSectionGraphMode,
  offset: number,
  nonce: number,
) {
  const [profileLine, setProfileLine] = useState<Vector3[]>([]);
  const [graphLines, setGraphLines] = useState<GraphLine[]>([]);

  useEffect(() => {
    void nonce;
    const coordinates = tool.getProfileLine(mode, offset);

    const mas = CoordinateManager.getInstance().getMAS();
    const xOffset = mode === 'cross-section' ? tool.crossSectionLeftWidth : 0;

    if (coordinates.length > 1) {
      const newGraphLines: GraphLine[] = [];
      for (const data of tool.getAssets()) {
        const result = getIntersectedEdges(coordinates, data, mas, xOffset);
        newGraphLines.push({
          color: data.color,
          id: data.id,
          name: data.asset.name,
          segments: result,
        });
      }

      tool.setEdges(newGraphLines.flatMap((x) => x.segments.map((y) => y.original)));
      setGraphLines(newGraphLines);

      const profileLine = coordinates
        .map((x) => CoordinateManager.getInstance().projectCoordsToLocal(x))
        .map((x) => x.setZ(0));

      setProfileLine(profileLine);
    }
  }, [tool, mode, nonce, offset]);

  return { graphLines, profileLine };
}

export type ProfileSectionLineData = {
  color: string;
  original: [Vector3, Vector3];
  projected: [Vector2, Vector2];
};

function GraphContent(props: {
  graphLines: GraphLine[];
  profileLine: Vector3[];
  tool: ProfileSectionTool;
  mode: ProfileSectionGraphMode;
  exportPdfRef: React.RefObject<SVGSVGElement>;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const transformRef = useRef<d3.ZoomTransform | null>(null);
  const lastCursorPosition = useRef<Vector2 | null>(null);
  const measureFromPosition = useRef<Vector2 | null>(null);
  const exportReportDialog = useDialog();

  useEffect(() => {
    const btn = props.exportPdfRef?.current;

    if (!btn) {
      return;
    }

    const handler = () => {
      if (!svgRef.current) {
        return;
      }

      exportReportDialog.show();
    };

    btn.addEventListener('click', handler);

    return () => {
      btn.removeEventListener('click', handler);
    };
  }, [
    props.exportPdfRef,
    props.graphLines,
    props.profileLine,
    props.tool,
    exportReportDialog,
    props.mode,
  ]);

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }

    // Reset data from last graph.
    lastCursorPosition.current = null;
    measureFromPosition.current = null;

    const xOffset = props.mode === 'cross-section' ? props.tool.crossSectionLeftWidth : 0;
    const context = profileSectionGraphUtils.createGraph(
      svgRef.current,
      props.profileLine,
      props.graphLines,
      xOffset,
      props.tool.distanceFromStart,
    );

    profileSectionGraphUtils.createZoomFunction(
      props.tool,
      context,
      transformRef,
      lastCursorPosition,
      measureFromPosition,
    );
    profileSectionGraphUtils.createCursorFunction(
      props.tool,
      context,
      transformRef,
      lastCursorPosition,
      measureFromPosition,
    );
  }, [props.profileLine, props.graphLines, props.tool, props.mode]);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
      {exportReportDialog.render(
        <ExportProfileSectionReportDialog
          dialog={exportReportDialog}
          graphLines={props.graphLines}
          mode={props.mode}
          originalSvg={svgRef}
          profileLine={props.profileLine}
          tool={props.tool}
        />,
      )}
    </div>
  );
}

type IntervalDistributionType = '2d' | '3d';

const Graph = (
  props: Props & { mode: ProfileSectionGraphMode; exportPdfRef: React.RefObject<SVGSVGElement> },
) => {
  const unitSymbols = useUnitSymbols();
  const [interval, setInterval] = useState(5);
  const [intervalLabel, setIntervalLabel] = useState(interval.toString());
  const [leftWidth, setLeftWidth] = useState(props.tool.crossSectionLeftWidth.toString());
  const [rightWidth, setRightWidth] = useState(props.tool.crossSectionRightWidth.toString());
  const [nonce, setNonce] = useNonce();

  const intervalDistributionOptions = useMemo<{ id: IntervalDistributionType; name: string }[]>(
    () => [
      { id: '2d', name: t('profileSection.intervalDistribution2d', { ns: 'skyview' }) },
      { id: '3d', name: t('profileSection.intervalDistribution3d', { ns: 'skyview' }) },
    ],
    [],
  );

  const [intervalDistributionMode, setIntervalDistributionMode] =
    useState<IntervalDistributionType>(intervalDistributionOptions[0].id);

  const profileLineLength = useMemo(() => {
    void nonce, intervalDistributionMode;
    const coordinates = props.tool.getProfileLine('length-profile');
    return getPolyLineLength(coordinates.map((x) => new Vector3(x.easting, x.northing, x.height)));
  }, [intervalDistributionMode, props.tool, nonce]);

  const [offsetLabel, setOffsetLabel] = useState('0');
  const [offset, setOffset] = useState(0);

  const onOffsetUpdated = useCallback(
    (offset: number) => {
      setOffsetLabel(offset.toString());
      setOffset(MathUtils.clamp(offset, 0, profileLineLength));
      setNonce();
    },
    [setNonce, profileLineLength],
  );
  const graphRef = useRef<HTMLDivElement>(null);
  const throttledSetNonce = useMemo(
    () => throttle(setNonce, 750, { leading: false, trailing: true }),
    [setNonce],
  );

  useSubscribe(SubscriptionTopic.OnSceneChangeCompleted, throttledSetNonce);
  useSubscribe(SubscriptionTopic.SkyviewAssetVisibilityChanged, throttledSetNonce);
  useSubscribe(SubscriptionTopic.AssetChanged, throttledSetNonce);
  useSubscribe(SubscriptionTopic.ProfileSectionUpdated, throttledSetNonce);
  useSubscribe(SubscriptionTopic.ProfileSectionOffsetUpdated, onOffsetUpdated);
  useResizeObserver(graphRef.current?.parentElement ?? null, () => throttledSetNonce());

  const { graphLines, profileLine } = useLengthProfileResult(props.tool, props.mode, offset, nonce);

  useEffect(() => {
    props.tool.flattenProfileLine = intervalDistributionMode === '2d';
    setOffset(0);
    setOffsetLabel('0');
    setNonce();
  }, [intervalDistributionMode, props.tool, setNonce]);

  useEffect(() => {
    void leftWidth, rightWidth, profileLine;
    props.tool.setCrossSectionsPreview(props.mode === 'cross-section' ? interval : undefined);
  }, [interval, props.mode, props.tool, leftWidth, rightWidth, profileLine]);

  const colorDialog = useDialog();

  const [activeGraphLine, setActiveGraphLine] = useState<GraphLine>();

  const onColorSelected = useCallback(
    (newColor: RgbArray) => {
      if (activeGraphLine) {
        activeGraphLine.color = new Color(...newColor);
        props.tool.setAssetColor(activeGraphLine.id, new Color(...newColor));
      }
      setNonce();
      publish(SubscriptionTopic.ProfileSectionUpdated);
    },
    [activeGraphLine, setNonce, props.tool],
  );

  const selectedColor = useMemo(
    () => activeGraphLine?.color.toArray() as RgbArray,
    [activeGraphLine],
  );

  if (graphLines.length > 0) {
    return (
      <div
        ref={graphRef}
        style={{ width: '100%', height: '100%', flex: 1, minWidth: 0, minHeight: 0 }}
      >
        <GraphContainer direction="row" spacing={1}>
          {props.mode === 'cross-section' && (
            <CrossSectionSettings direction="column" spacing={1}>
              <LabelledContainer
                text={`${t('profileSection.section', { ns: 'skyview' })} ${getSectionLabel(offset)}`}
              >
                <TextBox
                  min={0}
                  step={interval}
                  type="number"
                  value={offsetLabel}
                  onBlur={() => setOffsetLabel(offset.toString())}
                  onChange={(e) => {
                    setOffsetLabel(e.target.value);
                    if (isValidNumber(e.target.value)) {
                      const value = Number(e.target.value);
                      const newOffset = Math.round(value / interval) * interval;
                      setOffset(MathUtils.clamp(newOffset, 0, profileLineLength));
                      setNonce();
                    }
                  }}
                />
              </LabelledContainer>

              <LabelledContainer
                text={t('profileSection.interval', { ns: 'skyview', unit: unitSymbols.length })}
              >
                <TextBox
                  type="number"
                  value={intervalLabel}
                  onChange={(e) => {
                    setIntervalLabel(e.target.value);
                    if (isValidNumber(e.target.value)) {
                      setInterval(Math.max(Number(e.target.value), 0.01));
                      setNonce();
                    }
                  }}
                />
              </LabelledContainer>

              <LabelledContainer text={t('profileSection.intervalDistribution', { ns: 'skyview' })}>
                <Select
                  options={intervalDistributionOptions}
                  value={intervalDistributionMode}
                  onChange={(e) =>
                    setIntervalDistributionMode(e.target.value as IntervalDistributionType)
                  }
                />
              </LabelledContainer>

              <LabelledContainer
                text={t('profileSection.widthLeft', { ns: 'skyview', unit: unitSymbols.length })}
              >
                <TextBox
                  min={0}
                  type="number"
                  value={leftWidth}
                  onChange={(e) => {
                    setLeftWidth(e.target.value);
                    if (isValidNumber(e.target.value)) {
                      props.tool.crossSectionLeftWidth = Math.max(Number(e.target.value), 0);
                      setNonce();
                    }
                  }}
                />
              </LabelledContainer>
              <LabelledContainer
                text={t('profileSection.widthRight', { ns: 'skyview', unit: unitSymbols.length })}
              >
                <TextBox
                  min={0}
                  type="number"
                  value={rightWidth}
                  onChange={(e) => {
                    setRightWidth(e.target.value);
                    if (isValidNumber(e.target.value)) {
                      props.tool.crossSectionRightWidth = Math.max(Number(e.target.value), 0);
                      setNonce();
                    }
                  }}
                />
              </LabelledContainer>
            </CrossSectionSettings>
          )}
          <GraphContent
            exportPdfRef={props.exportPdfRef}
            graphLines={graphLines}
            mode={props.mode}
            profileLine={profileLine}
            tool={props.tool}
          />
          <LegendsPanel spacing={0.5}>
            {graphLines.map((x) => (
              <Stack
                alignItems="flex-start"
                direction="row"
                justifyContent="center"
                key={x.id}
                spacing={0.5}
              >
                <ColorIcon
                  data-color={x.color.toArray() as RgbArray}
                  title={t('section.attributes.attributeColorTypeSection.changeColorIcon', {
                    ns: 'skyviewDesign',
                  })}
                  onClick={() => {
                    setActiveGraphLine(x);
                    colorDialog.show();
                  }}
                />
                <LayerLabel>{x.name}</LayerLabel>
              </Stack>
            ))}
            {colorDialog.render(
              <ColorDialog
                dialog={colorDialog}
                value={selectedColor}
                onConfirm={onColorSelected}
              />,
            )}
          </LegendsPanel>
        </GraphContainer>
      </div>
    );
  }

  return (
    <GraphContainer>
      <IconPanel
        header={{
          icon: {
            icon: ['fal', props.mode === 'length-profile' ? 'line-chart' : 'chart-area'],
            opacity: '50%',
            size: '4x',
          },
        }}
        responsiveness={{ compressed: true }}
      >
        {{
          title:
            props.mode === 'length-profile'
              ? t('profileSection.lengthProfile', { ns: 'skyview' })
              : t('profileSection.crossSection', { ns: 'skyview' }),
          info: t('profileSection.toolHint', { ns: 'skyview' }),
        }}
      </IconPanel>
    </GraphContainer>
  );
};

type Option = {
  id: string;
  name: string;
  asset: ElementObj;
};

const CrossSectionSettings = styled(Stack)`
  ${ThinScrollbar};
  overflow-y: scroll;
  padding-right: 0.5em;
`;

const LegendsPanel = styled(Stack)`
  ${ThinScrollbar};
  overflow-y: scroll;
  max-width: 350px;
`;

const LayerLabel = styled.label`
  flex: 1;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const ImportLineDialog = (props: {
  tool: ProfileSectionTool;
  mode: ProfileSectionGraphMode;
  dialog: ReturnType<typeof useDialog>;
}) => {
  const { assets } = useAssets<ElementObj>(['design', 'dxf', 'line', 'glb', 'model']);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const options = useMemo<Option[]>(() => {
    return assets.map((asset) => ({
      asset,
      id: asset.uuid,
      name: `${asset.name} (${asset.assetType.toUpperCase()})`,
    }));
  }, [assets]);
  const [selectedOptionId, setSelectedOptionId] = useState<string>();

  const importAsset = useCallback(() => {
    const cb = async () => {
      try {
        setLoading(true);
        const asset = options.find((x) => x.asset.uuid === selectedOptionId)?.asset;

        if (!asset) {
          return;
        }

        await AssetVisibilityManager.getInstance().toggleAsset(asset.uuid, true, false);

        let positionArray: Vector3[] | undefined;

        if (asset instanceof Geometry && !asset.isArea) {
          props.tool.setProfileLine(asset.getWorldPoints(), props.mode, asset.name);
          props.dialog.hide();
          return;
        } else {
          asset.traverse((child) => {
            if (
              (child instanceof LineSegments || child instanceof Line) &&
              child.geometry instanceof BufferGeometry &&
              (child.material as MeshBasicMaterial).wireframe !== true
            ) {
              positionArray = bufferAttributeUtils.getArray(child, true);
            }
          });
        }

        const noLineError = t('profileSection.noLineFound', { ns: 'skyview' });

        if (!isDefined(positionArray) || positionArray.length < 2) {
          setErrorMessage(noLineError);
          return;
        }

        let lastEnd: Vector3 = positionArray[0];
        const polyLine: Vector3[] = [lastEnd];

        for (const [start, end] of pairIterator(positionArray)) {
          if (polyLine.length === 1 || lastEnd.distanceToSquared(start) < mathematicalPrecision) {
            polyLine.push(end);
          } else {
            break;
          }

          lastEnd = end;
        }

        if (polyLine.length < 2) {
          setErrorMessage(noLineError);
          return;
        }

        props.tool.setProfileLine(polyLine, props.mode, asset.name);
        props.dialog.hide();
      } catch (error) {
        console.error(
          'Error importing asset',
          error instanceof Error ? error.message : 'Unknown error',
        );
        setErrorMessage(t('unexpectedError', { ns: 'common' }));
      } finally {
        setLoading(false);
      }
    };

    void cb();
  }, [options, selectedOptionId, props.dialog, props.tool, props.mode]);

  return (
    <Dialog width={300} onClose={props.dialog.hide}>
      {{
        header: t('profileSection.importProfileLine', { ns: 'skyview' }),
        content: (
          <Stack spacing={1}>
            <DropdownSuggestion
              disabled={loading}
              placeholder={t('search', { ns: 'common' })}
              portalToBody={true}
              suggestions={options}
              value={selectedOptionId}
              width={'100%'}
              onSuggestionSelected={(suggestion) => setSelectedOptionId(suggestion.id)}
            />
            {isDefined(errorMessage) && (
              <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
                {errorMessage}
              </InfoBox>
            )}
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={loading || !selectedOptionId}
                loading={loading}
                variant="contained"
                onClick={importAsset}
              >
                {t('import', { ns: 'common' })}
              </Button>

              <Button variant="text" onClick={props.dialog.hide}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export const ProfileSectionToolMenu = (props: Props) => {
  const [graphMode, setGraphMode] = useState<ProfileSectionGraphMode>('length-profile');
  const importLineDialog = useDialog();
  const [measureMode, setProfileSectionMeasureMode] = useState<ProfileSectionMeasureMode>(
    props.tool.measureMode,
  );
  const exportPdfIconRef = useRef<SVGSVGElement>(null);

  const theme = useTheme();
  return (
    <Component direction="column" spacing={0}>
      <RightPanelHeader
        icon={['fal', 'chart-area']}
        name={t('profileSection.tooltip.title', { ns: 'skyviewToolbar' })}
      />
      <Content direction={'row'} spacing={0}>
        <Toolbar spacing={0.2}>
          <StyledIcon
            $active={false}
            color={theme.color.gray.darkest}
            fixedWidth={true}
            fontSize={24}
            icon={['fal', 'pencil']}
            size="1x"
            title={t('profileSection.newProfileLine', { ns: 'skyview' })}
            onClick={() => props.tool.resetProfileLine()}
            onHoverStyle={{ icon: ['fad', 'pencil'] }}
          />
          <StyledIcon
            $active={false}
            color={theme.color.gray.darkest}
            fixedWidth={true}
            fontSize={24}
            icon={['fal', 'file-import']}
            size="1x"
            title={t('profileSection.importProfileLine', { ns: 'skyview' })}
            onClick={() => importLineDialog.show()}
            onHoverStyle={{ icon: ['fad', 'file-import'] }}
          />
          <StyledIcon
            $active={graphMode === 'length-profile'}
            color={theme.color.gray.darkest}
            data-active={true}
            fixedWidth={true}
            fontSize={24}
            icon={['fal', 'line-chart']}
            inverse={true}
            size="1x"
            title={t('profileSection.lengthProfile', { ns: 'skyview' })}
            onClick={() => setGraphMode('length-profile')}
            onHoverStyle={{ icon: ['fad', 'line-chart'] }}
          />
          <StyledIcon
            $active={graphMode === 'cross-section'}
            color={theme.color.gray.darkest}
            fixedWidth={true}
            fontSize={24}
            icon={['fal', 'chart-area']}
            size="1x"
            title={t('profileSection.crossSection', { ns: 'skyview' })}
            onClick={() => setGraphMode('cross-section')}
            onHoverStyle={{ icon: ['fad', 'chart-area'] }}
          />
          <StyledIcon
            $active={measureMode === 'distance'}
            color={theme.color.gray.darkest}
            fixedWidth={true}
            fontSize={24}
            icon={['fal', 'ruler']}
            size="1x"
            title={t('profileSection.measureDistance', { ns: 'skyview' })}
            onClick={() => {
              props.tool.measureMode = 'distance';
              setProfileSectionMeasureMode('distance');
            }}
            onHoverStyle={{ icon: ['fad', 'ruler'] }}
          />
          {FeatureFlagsManager.instance.isEnabled('SK-2782-length-profile-area-calculation') && (
            <StyledIcon
              $active={measureMode === 'area'}
              color={theme.color.gray.darkest}
              fixedWidth={true}
              fontSize={24}
              icon={['fal', 'triangle']}
              size="1x"
              title={t('profileSection.measureArea', { ns: 'skyview' })}
              onClick={() => {
                props.tool.measureMode = 'area';
                setProfileSectionMeasureMode('area');
              }}
              onHoverStyle={{ icon: ['fad', 'triangle'] }}
            />
          )}
          <StyledIcon
            $active={false}
            color={theme.color.gray.darkest}
            fixedWidth={true}
            fontSize={24}
            icon={['fal', 'file-pdf']}
            ref={exportPdfIconRef}
            size="1x"
            title={t('profileSection.exportPdf', { ns: 'skyview' })}
            onHoverStyle={{ icon: ['fad', 'file-pdf'] }}
          />
        </Toolbar>
        <Graph exportPdfRef={exportPdfIconRef} mode={graphMode} tool={props.tool} />
        {importLineDialog.render(
          <ImportLineDialog dialog={importLineDialog} mode={graphMode} tool={props.tool} />,
        )}
      </Content>
    </Component>
  );
};

const Component = styled(Stack)`
  height: 100%;
`;

const Content = styled(Stack)`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Toolbar = styled(Stack)`
  padding: 8px;
  border-right: solid 1px ${(props) => props.theme.color.gray.light};
`;

const GraphContainer = styled(Stack)`
  flex: 1;
  overflow: hidden;
  padding: 0.8em;
  height: 100%;
`;

const StyledIcon = styled(Icon)<{ $active: boolean }>`
  border-radius: 4px;
  padding: 4px;
  width: 24px;
  height: 24px;

  ${(props) =>
    props.$active &&
    css`
      background-color: ${props.theme.color.gray.darkest};
      color: #ffffff;
    `}
`;
